if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const selectedVariant = this.getVariantFromCurrentSelection();
        this.activateVariantMedia(selectedVariant);

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        const variantId = this.getVariantIdFromSelection();

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage, variantId),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      getPreviewVariantByFirstOption() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return null;
        const variants = variantSelects.getVariantData();
        if (!variants) return null;
        const first = variantSelects.querySelector('fieldset, .select__select');
        let firstValue = null;
        if (first) {
          if (first.tagName === 'FIELDSET') {
            firstValue = first.querySelector('input:checked')?.value || null;
          } else {
            firstValue = first.value || null;
          }
        }
        if (!firstValue) return null;
        return (
          variants.find((v) => v.available && v.options && v.options[0] === firstValue) ||
          variants.find((v) => v.options && v.options[0] === firstValue) ||
          null
        );
      }

      getVariantFromCurrentSelection() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return null;

        const variants = variantSelects.getVariantData();
        if (!variants) return null;

        const fieldsetsAndSelects = Array.from(variantSelects.querySelectorAll('fieldset, .select__select'));
        const currentValues = fieldsetsAndSelects.map((el) => {
          if (el.tagName === 'FIELDSET') {
            return el.querySelector('input:checked')?.value;
          } else {
            return el.value;
          }
        });

        return (
          variants.find((variant) => {
            return variant.options.every((val, index) => val === currentValues[index]);
          }) || null
        );
      }

      getVariantFeaturedMediaId(variant) {
        return variant?.featured_media?.id || variant?.featured_image?.id || variant?.image_id || null;
      }

      getVariantFeaturedImageSrc(variant) {
        const featuredImage = variant?.featured_image;
        if (!featuredImage) return null;
        if (typeof featuredImage === 'string') return featuredImage;
        return featuredImage?.src || featuredImage?.url || featuredImage?.preview_image?.src || null;
      }

      normalizeImageUrlForMatch(url) {
        if (!url || typeof url !== 'string') return null;
        const noQuery = url.split('?')[0];
        const parts = noQuery.split('/');
        const filename = parts[parts.length - 1] || '';
        if (!filename) return null;
        const cleanedFilename = filename.replace(/(_\d+x\d+|_\d+x|_x\d+)(?=\.[a-z0-9]+$)/i, '');
        return cleanedFilename.toLowerCase();
      }

      resolveGalleryMediaIdByImageSrc(imageSrc, container = this) {
        if (!imageSrc || !container?.querySelectorAll) return null;
        const needle = this.normalizeImageUrlForMatch(imageSrc);
        if (!needle) return null;

        const images = Array.from(container.querySelectorAll('li[data-media-id] img, li[data-target] img'));
        const match = images.find((img) => {
          const currentUrl = img.currentSrc || img.getAttribute('src') || '';
          const currentNormalized = this.normalizeImageUrlForMatch(currentUrl);
          if (currentNormalized && currentNormalized === needle) return true;

          const srcset = img.getAttribute('srcset') || '';
          if (!srcset) return false;
          const srcsetUrls = srcset
            .split(',')
            .map((entry) => entry.trim().split(/\s+/)[0])
            .filter(Boolean);

          return srcsetUrls.some((url) => this.normalizeImageUrlForMatch(url) === needle);
        });

        const li = match?.closest?.('li[data-media-id], li[data-target]');
        return li ? li.dataset.mediaId || li.dataset.target : null;
      }

      resolveGalleryMediaId(mediaId, container = this) {
        if (!mediaId || !container?.querySelectorAll) return null;

        const candidates = Array.from(container.querySelectorAll('li[data-media-id], [data-target]'));
        const match = candidates.find((item) => {
          const itemId = item.dataset.mediaId || item.dataset.target;
          return (
            itemId === `${this.sectionId}-${mediaId}` ||
            itemId === `${this.dataset.section}-${mediaId}` ||
            itemId === `${mediaId}` ||
            itemId?.endsWith(`-${mediaId}`)
          );
        });

        return match ? match.dataset.mediaId || match.dataset.target : null;
      }

      activateVariantMedia(variant, prepend = false) {
        const mediaId = this.getVariantFeaturedMediaId(variant);
        const mediaGallery = this.querySelector('media-gallery');
        const galleryMediaId =
          this.resolveGalleryMediaId(mediaId, mediaGallery) ||
          this.resolveGalleryMediaIdByImageSrc(this.getVariantFeaturedImageSrc(variant), mediaGallery);

        if (galleryMediaId) {
          mediaGallery?.setActiveMedia?.(galleryMediaId, prepend);
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, variantId = null) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        if (variantId) {
          params.push(`variant=${variantId}`);
        }

        return `${url}?${params.join('&')}`;
      }

      getVariantIdFromSelection() {
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return null;

        const variants = variantSelects.getVariantData();
        if (!variants) return null;

        const fieldsetsAndSelects = Array.from(variantSelects.querySelectorAll('fieldset, .select__select'));
        const currentValues = fieldsetsAndSelects.map(el => {
          if (el.tagName === 'FIELDSET') {
            return el.querySelector('input:checked')?.value;
          } else {
            return el.value;
          }
        });

        const matchingVariant = variants.find(variant => {
          return variant.options.every((val, index) => val === currentValues[index]);
        });

        return matchingVariant?.id;
      }

      updateOptionValues(html) {
        if (this.variantSelectors?.dataset?.variantPickerMode) return;

        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);
          const selectedVariant = variant || this.getVariantFromCurrentSelection();

          this.pickupAvailability?.update(selectedVariant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, selectedVariant?.id);
          this.updateVariantInputs(selectedVariant?.id);

          if (!variant) {
            const preview = selectedVariant || this.getPreviewVariantByFirstOption();
            this.updateMedia(html, this.getVariantFeaturedMediaId(preview), this.getVariantFeaturedImageSrc(preview));
            this.setUnavailable();
            return;
          }

          this.updateMedia(
            html,
            this.getVariantFeaturedMediaId(selectedVariant),
            this.getVariantFeaturedImageSrc(selectedVariant)
          );

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant: selectedVariant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId, variantFeaturedImageSrc = null) {
        const mediaGallery = this.querySelector('media-gallery');
        const mediaGallerySource = mediaGallery?.querySelector('[id^="Slider-Gallery-"]');
        const mediaGalleryDestination = html.querySelector('media-gallery [id^="Slider-Gallery-"]');
        const thumbnailSource = mediaGallery?.querySelector('[id^="Slider-Thumbnails-"]');
        const thumbnailDestination = html.querySelector('media-gallery [id^="Slider-Thumbnails-"]');

        const syncMediaList = (sourceList, destinationList) => {
          if (!sourceList || !destinationList) return;

          const refreshSourceData = () => {
            if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
            const sourceItems = Array.from(sourceList.querySelectorAll('li[data-media-id], li[data-target]'));
            const getItemId = (item) => item.dataset.mediaId || item.dataset.target;
            const sourceSet = new Set(sourceItems.map((item) => getItemId(item)));
            const sourceMap = new Map(sourceItems.map((item, index) => [getItemId(item), { item, index }]));
            return [sourceItems, sourceSet, sourceMap, getItemId];
          };

          let [sourceItems, sourceSet, sourceMap, getItemId] = refreshSourceData();
          const destinationItems = Array.from(
            destinationList.querySelectorAll('li[data-media-id], li[data-target]')
          );
          const destinationSet = new Set(destinationItems.map((item) => getItemId(item)));
          let shouldRefresh = false;

          for (let i = destinationItems.length - 1; i >= 0; i--) {
            const destinationItemId = getItemId(destinationItems[i]);
            if (!sourceSet.has(destinationItemId)) {
              sourceList.prepend(destinationItems[i]);
              shouldRefresh = true;
            }
          }

          for (let i = 0; i < sourceItems.length; i++) {
            const sourceItemId = getItemId(sourceItems[i]);
            if (!destinationSet.has(sourceItemId)) {
              sourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          if (shouldRefresh) [sourceItems, sourceSet, sourceMap, getItemId] = refreshSourceData();

          destinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(getItemId(destinationItem));

            if (sourceData && sourceData.index !== destinationIndex) {
              sourceList.insertBefore(
                sourceData.item,
                sourceList.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );
              [sourceItems, sourceSet, sourceMap, getItemId] = refreshSourceData();
            }
          });

          sourceList.closest('slider-component')?.resetPages?.();
        };

        syncMediaList(mediaGallerySource, mediaGalleryDestination);
        syncMediaList(thumbnailSource, thumbnailDestination);

        const activeMediaId =
          this.resolveGalleryMediaId(variantFeaturedMediaId, mediaGallery) ||
          this.resolveGalleryMediaIdByImageSrc(variantFeaturedImageSrc, mediaGallery) ||
          mediaGalleryDestination?.querySelector('li.is-active[data-media-id]')?.dataset.mediaId ||
          mediaGalleryDestination?.querySelector('li[data-media-id]')?.dataset.mediaId;

        if (activeMediaId) {
          mediaGallery?.setActiveMedia?.(activeMediaId, true);
        }

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
